// src/components/idcard/QrCode.tsx — scannable QR rendered as a crisp SVG path.
//
// Ported from the prototype's idcard-qr.jsx. Encodes whatever string it's
// given (the card uses the bare 9-digit card number — door/gate scanners
// expect the raw number, not a URL). Output is a single <path> so it scales
// to any size and survives screenshot / PDF export. EC level M.

import { useMemo } from "react";
import qrcode from "qrcode-generator";

export interface QrCodeProps {
  value: string;
  size?: number;
  fg?: string;
  bg?: string;
  /** Quiet-zone modules around the code. */
  quiet?: number;
}

export function QrCode({ value, size = 132, fg = "#111111", bg = "transparent", quiet = 2 }: QrCodeProps) {
  const { path, count } = useMemo(() => {
    if (!value) return { path: "", count: 0 };
    try {
      const qr = qrcode(0, "M"); // type 0 = auto-fit, EC level M
      qr.addData(String(value));
      qr.make();
      const n = qr.getModuleCount();
      let d = "";
      for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
          if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
        }
      }
      return { path: d, count: n };
    } catch (e) {
      console.warn("QR encode failed", e);
      return { path: "", count: 0 };
    }
  }, [value]);

  const total = count + quiet * 2;

  // Fallback box if encoding produced nothing (e.g. empty value).
  if (!path) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 6,
          background: "#F1ECEF",
          border: "1px dashed #C9BDC6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'IBM Plex Mono',monospace",
          fontSize: 10,
          color: "#9A8E96",
          textAlign: "center",
          padding: 8,
          boxSizing: "border-box",
          lineHeight: 1.4,
        }}
      >
        QR<br />
        {value || "no card no."}
      </div>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for card number ${value}`}
      style={{ display: "block", borderRadius: 4 }}
    >
      {bg !== "transparent" && <rect width={total} height={total} fill={bg} />}
      <g transform={`translate(${quiet} ${quiet})`}>
        <path d={path} fill={fg} />
      </g>
    </svg>
  );
}
